/* =============================================================================
 *  mailSyncModel - pure logic behind the mail sync-health UI.
 *
 *  Two independent concerns live here so they can be unit-tested without
 *  React or Firebase:
 *   1. The getMailSyncHealth payload: validation, severity classification,
 *      the copy shown in the pill / strip / details, age formatting.
 *   2. The checkNewEmails (manual refresh) payload: turning the per-mailbox
 *      results[] into one honest, plain-language status line.
 * ============================================================================= */

export type MailboxStatus = 'ok' | 'degraded' | 'down' | 'paused';
export type OverallStatus = 'ok' | 'degraded' | 'down';
export type IssueSeverity = 'info' | 'warn' | 'error';

export interface MailSyncIssue {
  code: string;
  severity: IssueSeverity;
  message: string;
}

export interface MailboxHealth {
  mailbox: string;
  status: MailboxStatus;
  summary: string;
  issues: MailSyncIssue[];
  push: { subscribed: boolean; expiresAt: string | null };
  delta: { lastSyncAt: string | null; lastError: string | null };
  sweep: { lastSyncAt: string | null; ok: boolean | null; lastError: string | null };
  pausedAt: string | null;
  pausedReason: string | null;
}

export interface MailSyncHealthData {
  generatedAt: string | null;
  overall: OverallStatus;
  mailboxes: MailboxHealth[];
}

/** How loud the UI should be. `info` = informational only (paused mailboxes). */
export type HealthLevel = 'ok' | 'info' | 'warn' | 'error';

const MAILBOX_STATUSES: MailboxStatus[] = ['ok', 'degraded', 'down', 'paused'];
const OVERALL_STATUSES: OverallStatus[] = ['ok', 'degraded', 'down'];

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

/**
 * Validate + default a getMailSyncHealth response. Returns null when the
 * payload is not recognisable (stale deploy, proxy page) so the caller keeps
 * its last good value instead of rendering garbage.
 */
export function normalizeHealth(raw: any): MailSyncHealthData | null {
  if (!raw || typeof raw !== 'object' || raw.ok === false) return null;
  if (!Array.isArray(raw.mailboxes)) return null;
  const overall: OverallStatus = OVERALL_STATUSES.includes(raw.overall) ? raw.overall : 'degraded';
  const mailboxes: MailboxHealth[] = [];
  for (const m of raw.mailboxes) {
    if (!m || typeof m !== 'object' || typeof m.mailbox !== 'string' || !m.mailbox) continue;
    const status: MailboxStatus = MAILBOX_STATUSES.includes(m.status) ? m.status : 'degraded';
    mailboxes.push({
      mailbox: m.mailbox,
      status,
      summary: typeof m.summary === 'string' ? m.summary : '',
      issues: Array.isArray(m.issues)
        ? m.issues
            .filter((i: any) => i && typeof i.message === 'string')
            .map((i: any) => ({
              code: String(i.code || ''),
              severity: (['info', 'warn', 'error'].includes(i.severity) ? i.severity : 'warn') as IssueSeverity,
              message: i.message,
            }))
        : [],
      push: { subscribed: !!m.push?.subscribed, expiresAt: str(m.push?.expiresAt) },
      delta: { lastSyncAt: str(m.delta?.lastSyncAt), lastError: str(m.delta?.lastError) },
      sweep: {
        lastSyncAt: str(m.sweep?.lastSyncAt),
        ok: typeof m.sweep?.ok === 'boolean' ? m.sweep.ok : null,
        lastError: str(m.sweep?.lastError),
      },
      pausedAt: str(m.pausedAt),
      pausedReason: str(m.pausedReason),
    });
  }
  return { generatedAt: str(raw.generatedAt), overall, mailboxes };
}

/** Mailboxes that need a human's eye (anything that is not ok). */
export function attentionMailboxes(h: MailSyncHealthData | null): MailboxHealth[] {
  return h ? h.mailboxes.filter((m) => m.status !== 'ok') : [];
}

/**
 * ok    - nothing to show.
 * info  - only PAUSED mailboxes are unhealthy and at least one other mailbox
 *         works: a known, parked condition (e.g. a deleted mailbox), not a fire.
 * error - something is down, or nothing is syncing at all.
 * warn  - degraded.
 */
export function healthLevel(h: MailSyncHealthData | null): HealthLevel {
  if (!h) return 'ok';
  const bad = attentionMailboxes(h);
  if (h.overall === 'ok' && bad.length === 0) return 'ok';
  if (bad.length === 0) {
    // overall says trouble but no mailbox rows explain it - stay honest.
    return h.overall === 'down' ? 'error' : 'warn';
  }
  const anyHealthy = h.mailboxes.some((m) => m.status === 'ok');
  if (anyHealthy && bad.every((m) => m.status === 'paused')) return 'info';
  // "Nothing is syncing" = every mailbox is down or paused (a degraded one is
  // still moving mail, just with problems).
  const anyWorking = h.mailboxes.some((m) => m.status === 'ok' || m.status === 'degraded');
  if (h.overall === 'down' || bad.some((m) => m.status === 'down') || !anyWorking) return 'error';
  return 'warn';
}

/** Changes whenever the SET of problems changes (not when ages tick), so a
 *  dismissed strip re-appears only for something new. */
export function issueSignature(h: MailSyncHealthData | null): string {
  return attentionMailboxes(h)
    .map((m) => `${m.mailbox}:${m.status}`)
    .sort()
    .join('|');
}

export function pillLabel(level: HealthLevel): string {
  if (level === 'error') return 'sync x';
  if (level === 'info') return 'sync i';
  return 'sync !';
}

/** "leads@webconstruct.com.au" -> "leads@…" for the one-line strip. */
export function shortMailbox(mailbox: string): string {
  const at = mailbox.indexOf('@');
  return at > 0 ? `${mailbox.slice(0, at)}@…` : mailbox;
}

function statusPhrase(m: MailboxHealth): string {
  if (m.status === 'paused') return 'paused';
  if (m.status === 'down') return 'not syncing';
  return 'is behind';
}

/** One-line copy for the mobile strip / the more-actions item. */
export function stripText(h: MailSyncHealthData | null): string {
  const bad = attentionMailboxes(h);
  if (bad.length === 0) return 'Sync issue - tap for details';
  if (bad.length === 1) return `Sync issue - ${shortMailbox(bad[0].mailbox)} ${statusPhrase(bad[0])} - tap for details`;
  return `Sync issue - ${bad.length} mailboxes need attention - tap for details`;
}

/**
 * Label for the phone [...] sheet's sync item. A parked (info-level) condition
 * reads as a note ("Mailbox paused - leads@…"), not as an alarm; only warn /
 * error keep the "Sync issue" wording.
 */
export function syncMenuLabel(h: MailSyncHealthData | null): string {
  if (healthLevel(h) !== 'info') return stripText(h).replace(' - tap for details', '');
  const bad = attentionMailboxes(h);
  if (bad.length === 1) return `Mailbox paused - ${shortMailbox(bad[0].mailbox)}`;
  return `${bad.length} mailboxes paused`;
}

/** "13d ago" / "8m ago" / "just now" / "never". */
export function formatAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return 'never';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'never';
  const mins = Math.floor(Math.max(0, now - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/** Tooltip for the refresh button when everything is healthy. */
export function healthyTitle(h: MailSyncHealthData | null, now: number = Date.now()): string {
  if (!h || h.overall !== 'ok') return '';
  const n = h.mailboxes.length;
  const noun = n === 1 ? 'mailbox' : 'mailboxes';
  return `all ${n} ${noun} healthy - checked ${formatAgo(h.generatedAt, now)}`;
}

/** "live sync 8m ago · background sync 13d ago · paused 70d ago" */
export function mailboxMeta(m: MailboxHealth, now: number = Date.now()): string {
  const parts = [
    `live sync ${formatAgo(m.delta.lastSyncAt, now)}`,
    `background sync ${formatAgo(m.sweep.lastSyncAt, now)}`,
  ];
  if (m.status === 'paused' && m.pausedAt) parts.push(`paused ${formatAgo(m.pausedAt, now)}`);
  return parts.join(' · ');
}

/* ─────────────────────────────────────────────────────────────────────────────
 *  Manual refresh (checkNewEmails) result -> honest status line
 * ──────────────────────────────────────────────────────────────────────────── */

export interface SyncResultRow {
  mailbox?: string;
  newEmails?: number;
  deletedEmails?: number;
  ok?: boolean;
  error?: string;
  notFound?: boolean;
  paused?: boolean;
}

export type SyncTone = 'ok' | 'warn' | 'error';
export interface SyncSummary {
  tone: SyncTone;
  text: string;
  /** true when every non-paused mailbox was actually checked (paused ones are a
   *  known, parked condition and do not count against it) */
  allOk: boolean;
}

function plainReason(e: unknown): string {
  const s = typeof e === 'string' ? e.replace(/\s+/g, ' ').trim() : '';
  return s.length > 120 ? `${s.slice(0, 117)}...` : s;
}

/**
 * The server words a paused row as error:'paused: <pausedReason>' (and never
 * sets notFound on it), where pausedReason itself ends in "- sync paused".
 * Shown verbatim that reads "paused (paused: ... - sync paused)": strip the
 * wrapper words so "paused" appears once.
 */
function pausedReason(e: unknown): string {
  return plainReason(e)
    .replace(/^paused:\s*/i, '')
    .replace(/\s*-\s*sync paused\.?$/i, '')
    .trim();
}

/** One mailbox's failure in plain language. */
export function describeSyncFailure(r: SyncResultRow): string {
  const name = String(r.mailbox || 'a mailbox');
  if (r.paused) {
    const reason = pausedReason(r.error);
    // The real paused row carries the not-found text but not the flag.
    if (r.notFound || /not found/i.test(reason)) return `${name}: paused (mailbox not found in Microsoft 365)`;
    return reason ? `${name}: paused - ${reason}` : `${name}: paused`;
  }
  if (r.notFound) return `${name}: mailbox not found in Microsoft 365`;
  const reason = plainReason(r.error);
  return `${name}: ${reason || 'sync failed'}`;
}

export function summarizeSyncResults(data: any): SyncSummary {
  const results: SyncResultRow[] = Array.isArray(data?.results)
    ? data.results.filter((r: any) => r && typeof r === 'object')
    : [];
  const totalNew = Number(data?.totalNew) || 0;
  const totalDeleted = Number(data?.totalDeleted) || 0;
  const countParts: string[] = [];
  if (totalNew > 0) countParts.push(`${totalNew} new`);
  if (totalDeleted > 0) countParts.push(`${totalDeleted} removed`);
  const counts = countParts.join(', ');

  // A PAUSED mailbox is a known, parked condition (e.g. a deleted mailbox the
  // sync gave up on), not a failed refresh: it was deliberately not checked, so
  // it must not turn every manual refresh amber or freeze "last synced" (the
  // health pill already reports it, quietly, at 'info'). Only mailboxes that
  // were actually attempted and failed are failures.
  const paused = results.filter((r) => r.paused === true);
  const active = results.filter((r) => r.paused !== true);
  const bad = active.filter((r) => r.ok === false);
  const pausedNote = paused.length
    ? ` (${paused.map((p) => shortMailbox(String(p.mailbox || 'a mailbox'))).join(', ')} paused)`
    : '';

  if (bad.length === 0) {
    // Every mailbox is paused: nothing was checked, so do not say "up to date".
    if (results.length > 0 && active.length === 0) {
      const detail = paused.map(describeSyncFailure).join('; ');
      return { tone: 'error', text: `nothing was checked - ${detail}`, allOk: false };
    }
    // No per-mailbox detail (older deploy): fall back to the aggregate flags
    // rather than reporting "up to date" over a known failure.
    const failed = Number(data?.failed) || 0;
    if (results.length === 0 && failed > 0) {
      return {
        tone: 'warn',
        text: `${failed} mailbox${failed === 1 ? '' : 'es'} failed to sync${counts ? ` (${counts} from the rest)` : ''}`,
        allOk: false,
      };
    }
    if (results.length === 0 && data?.mailboxes === 0) {
      return { tone: 'warn', text: 'no mailboxes are configured for sync', allOk: false };
    }
    return { tone: 'ok', text: (counts || 'up to date') + pausedNote, allOk: true };
  }

  const total = active.length;
  const checked = total - bad.length;
  const detail = bad.map(describeSyncFailure).join('; ');
  if (checked === 0) {
    return {
      tone: 'error',
      text: `couldn't check any of the ${total} mailbox${total === 1 ? '' : 'es'} - ${detail}${pausedNote}`,
      allOk: false,
    };
  }
  return {
    tone: 'warn',
    text: `checked ${checked} of ${total} mailboxes${counts ? ` (${counts})` : ''} - ${detail}${pausedNote}`,
    allOk: false,
  };
}
